import { useState } from "react";
import { ApiError } from "../../../shared/api/client";
import { completedDowntimeRequiresReason } from "../../../../shared/production/downtime";
import { productionGateway } from "../production-gateway";
import { commonDowntimeReasonIds } from "../downtime-common-reasons";
import { operatorDowntimeDraftKey } from "../operator-drafts";
import { downtimeReasonKey, missingDowntimeReasonTargets, resolveDowntimeReasonTarget, type DowntimeReasonDraft, type DowntimeReasonTarget } from "../downtime-reason-queue";
import type { DowntimeReasonRequest } from "../downtime-reason-submission";
import type { ReasonSubmitOptions } from "../useDowntimeReasonApproval";
import type { ProductionRun, ProductionReferenceData, ProductionDowntime } from "../models";
import type { OperatorRunActionsWorkspace } from "./useOperatorRunActionsWorkspace";

type ReasonOptions = {
  selected: ProductionRun | null;
  available: ProductionRun[];
  reasonCodes: ProductionReferenceData["reasons"];
  identity: { terminalCode: string; sessionId: string | undefined; operatorId: string; lineId: string; };
  notify: (message: string, tone?: "success" | "error") => void;
  applyConfirmedDowntime: (runId: string, downtime: ProductionDowntime) => void;
  refreshAuthoritativeRuns: (afterWrite?: boolean) => Promise<void>;
  load: (silent?: boolean, forceReference?: boolean) => Promise<void>;
  actions: Pick<OperatorRunActionsWorkspace, "recoverSessionMismatch" | "stopDowntime" | "setLastSavedAt">;
};

export function useOperatorDowntimeReasonWorkspace({ selected, available, reasonCodes, identity, notify, applyConfirmedDowntime, refreshAuthoritativeRuns, load, actions }: ReasonOptions) {
  const operationTerminalCode = identity.terminalCode;
  const { recoverSessionMismatch, stopDowntime, setLastSavedAt } = actions;
  const [requestedDowntimeReason, setRequestedDowntimeReason] = useState<DowntimeReasonTarget | null>(null);
  const [downtimeReasonDrafts, setDowntimeReasonDrafts] = useState<Record<string, DowntimeReasonDraft>>({});
  const [deferredDowntimeReasons, setDeferredDowntimeReasons] = useState<Set<string>>(() => new Set());
  const openDowntime = selected?.status === "ACTIVE"
    ? selected.downtimes.find((item) => !item.endedAt) ?? null
    : null;
  const recentReasonIds = commonDowntimeReasonIds(available, reasonCodes);
  const missingDowntimeReasons = missingDowntimeReasonTargets(selected);
  const pendingDowntimeReason = resolveDowntimeReasonTarget(
    selected,
    requestedDowntimeReason,
    deferredDowntimeReasons,
  );
  const visibleDowntimeReason = openDowntime ? null : pendingDowntimeReason;
  const visibleDowntimeReasonRecord = visibleDowntimeReason && selected?.id === visibleDowntimeReason.runId
    ? selected.downtimes.find((item) => item.id === visibleDowntimeReason.downtimeId) ?? null
    : null;
  const visibleDowntimeDraftKey = visibleDowntimeReason
    ? operatorDowntimeDraftKey(operationTerminalCode, identity.operatorId, identity.sessionId ?? "", identity.lineId, visibleDowntimeReason.runId, `downtime:${visibleDowntimeReason.downtimeId}`)
    : null;
  const visibleDowntimeReasonKey = visibleDowntimeReason
    ? downtimeReasonKey(visibleDowntimeReason)
    : null;
  const visibleDowntimeNote =
    openDowntime?.note?.trim() &&
      openDowntime.reasonName && openDowntime.note
        .trim()
        .localeCompare(openDowntime.reasonName.trim(), "tr-TR", {
          sensitivity: "base",
        }) !== 0
      ? openDowntime.note.trim()
      : null;
  const assignOpenReason = async (input: DowntimeReasonRequest, signal: AbortSignal) => {
    if (!selected || !openDowntime) return;
    try {
      const result = await productionGateway.assignDowntimeReason(selected.id, openDowntime.id, { ...input, expectedSessionId: identity.sessionId }, operationTerminalCode, signal);
      applyConfirmedDowntime(selected.id, result);
      notify("Duruş nedeni kaydedildi.");
      void refreshAuthoritativeRuns();
      void load(true, false);
      return result;
    } catch (error) {
      if (error instanceof ApiError && error.code === "PRODUCTION_SESSION_CHANGED") throw new Error(await recoverSessionMismatch(), { cause: error });
      throw error;
    }
  };
  const assignCompletedReason = async (input: DowntimeReasonRequest, approvalToken: string | undefined, options: ReasonSubmitOptions) => {
    if (!visibleDowntimeReason || !visibleDowntimeReasonKey) return;
    try {
      const downtime = await productionGateway.assignDowntimeReason(visibleDowntimeReason.runId, visibleDowntimeReason.downtimeId,
        { ...input, approvalToken, expectedSessionId: options.expectedSessionId }, operationTerminalCode, options.signal);
      if (options.signal.aborted) throw new DOMException("İşlem sonucu tekrar kontrol edilmeli.", "AbortError");
      applyConfirmedDowntime(visibleDowntimeReason.runId, downtime);
    } catch (error) {
      if (error instanceof ApiError && error.code === "PRODUCTION_SESSION_CHANGED") throw new Error(await recoverSessionMismatch(), { cause: error });
      throw error;
    }
    setLastSavedAt(new Date());
    setRequestedDowntimeReason((current) => current && downtimeReasonKey(current) !== visibleDowntimeReasonKey ? current : null);
    // The write has a server acknowledgement. Hide this completed
    // target until the fast ledger lane carries its confirmed reason,
    // instead of reopening the same sheet from a stale poll response.
    setDeferredDowntimeReasons((current) => new Set(current).add(visibleDowntimeReasonKey));
    setDowntimeReasonDrafts((current) => { const next = { ...current }; delete next[visibleDowntimeReasonKey]; return next; });
    void refreshAuthoritativeRuns();
    void load(true, false);
  };
  const resumeProduction = () => {
    if (!selected || !openDowntime) return;
    const downtimeId = openDowntime.id;
    void stopDowntime(downtimeId).then((ok) => {
      if (ok && !openDowntime.reasonCodeId && !openDowntime.reasonName && completedDowntimeRequiresReason({ ...openDowntime, endedAt: new Date().toISOString() })) {
        const target = { runId: selected.id, downtimeId };
        setDeferredDowntimeReasons((current) => {
          const next = new Set(current);
          next.delete(downtimeReasonKey(target));
          return next;
        });
        setRequestedDowntimeReason(target);
      }
    });
  };
  const requestMissingReason = () => {
    const target = missingDowntimeReasons[0];
    if (!target) return;
    setDeferredDowntimeReasons((current) => {
      const next = new Set(current);
      next.delete(downtimeReasonKey(target));
      return next;
    });
    setRequestedDowntimeReason(target);
  };
  const deferMissingReasons = () => {
    setDeferredDowntimeReasons((current) => {
      const next = new Set(current);
      missingDowntimeReasons.forEach((target) => next.add(downtimeReasonKey(target)));
      return next;
    });
    setRequestedDowntimeReason(null);
  };
  const changeReasonDraft = (draft: DowntimeReasonDraft) => {
    if (!visibleDowntimeReasonKey) return;
    setDowntimeReasonDrafts((current) => ({
      ...current,
      [visibleDowntimeReasonKey]: draft,
    }));
  };
  return {
    openDowntime, recentReasonIds, missingDowntimeReasons, visibleDowntimeReason, visibleDowntimeReasonRecord,
    visibleDowntimeDraftKey, visibleDowntimeReasonKey, visibleDowntimeNote, downtimeReasonDrafts,
    setRequestedDowntimeReason, assignOpenReason, assignCompletedReason, resumeProduction,
    requestMissingReason, deferMissingReasons, changeReasonDraft,
  };
}

export type OperatorDowntimeReasonWorkspace = ReturnType<typeof useOperatorDowntimeReasonWorkspace>;
